import { formatInTimeZone } from 'date-fns-tz';
import {
  COLORES_GONDOLA, GONDOLAS, NIVELES_ETIQUETAS, type GondolaCat,
} from '../../../domain/pg/catalogo';
import {
  desdoblarGrid, digitosPgDe, ojosPg, participacionDeGondola, participacionPg, pedazos,
} from '../../../domain/pg/gondola';
import type { FichaModerno, FotoPg, GondolaJson } from '../../../domain/pg/tipos';
import { fueraDeLaMaestra, nombreDeTienda } from '../../../domain/pg/tipos';
import {
  Dato, FotoPrivada, Seccion, SeccionPlegable, Senal,
} from '../../../components/DetalleExpandible';

const TZ = 'America/Lima';
const SIN = <span className="text-gray-400">—</span>;

const texto = (v: string | null | undefined) => {
  const t = (v ?? '').trim();
  return t === '' ? SIN : <>{t}</>;
};

function Fotos({ fotos, alt }: { fotos: readonly FotoPg[] | undefined; alt: string }) {
  const lista = fotos ?? [];
  if (lista.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3">
      {lista.map((f, i) => {
        const ruta = (f.ruta ?? '').trim();
        if (ruta === '') {
          return (
            <p key={i} className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
              Se tomó una foto y no se pudo subir{f.error != null && f.error !== '' ? `: ${f.error}` : '.'}
            </p>
          );
        }
        return <FotoPrivada key={ruta} path={ruta} alt={`${alt} ${i + 1}`} />;
      })}
    </div>
  );
}

/**
 * La góndola dibujada tal como la pintó el supervisor.
 *
 * Es el único dato del estudio que no se puede leer como número sin perder la mitad: **dónde** está
 * el espacio importa tanto como cuánto. Un 40 % en la repisa del piso y un 40 % a la altura de los
 * ojos son dos negociaciones distintas con la cadena, y un bloque partido en tres islas se ve mucho
 * menos que el mismo espacio junto.
 *
 * Cada celda es **medio módulo de ancho por un nivel de alto** (`cols = módulos × 2`), y se recorre
 * por filas de arriba abajo. El dígito apunta a `marcas[d-1]`; el `0` es espacio vacío.
 */
function Grilla({ g, gd }: { g: GondolaCat; gd: GondolaJson }) {
  const esp = gd.espacio;
  const p = participacionDeGondola(esp);

  if (esp === undefined || !p.medida) {
    const porque = p.motivo === 'largo_no_cuadra'
      ? 'La grilla no coincide con el tamaño del mueble. Cambiar los módulos o los niveles después '
        + 'de pintar la borra entera, sin avisar: lo más probable es que haya pasado eso.'
      : p.motivo === 'nada_pintado'
        ? 'El mueble está declarado pero no se pintó ninguna celda.'
        : 'No se llegó a medir el espacio de esta góndola.';
    return (
      <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        {porque}
      </p>
    );
  }

  const filas = desdoblarGrid(esp);
  const etiquetas = NIVELES_ETIQUETAS[esp.filas] ?? [];
  const pg = participacionPg(g.id, esp);
  const ojos = ojosPg(g.id, esp);
  const trozos = pedazos(esp, digitosPgDe(g.id, esp));

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <div className="inline-block min-w-0">
          {filas.map((fila, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-right text-xs text-gray-500">
                {etiquetas[i] ?? `nivel ${i + 1}`}
              </span>
              <div className="flex gap-0.5">
                {fila.map((d, c) => (
                  <span
                    key={c}
                    title={d === '0' ? 'vacío' : esp.marcas[Number(d) - 1] ?? `dígito ${d}`}
                    className="h-5 w-4 rounded-sm border border-white"
                    style={{
                      background: d === '0'
                        ? '#e5e7eb'
                        : COLORES_GONDOLA[Number(d) - 1] ?? '#8A959A',
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="mt-1 flex gap-2 pl-[7.5rem] text-xs text-gray-500">
            {Array.from({ length: Math.max(1, Math.round(esp.cols / 2)) }, (_, m) => (
              <span key={m} style={{ width: '2.25rem' }}>mód. {m + 1}</span>
            ))}
          </div>
        </div>
      </div>

      <ul className="space-y-1">
        {p.marcas.map((m) => (
          <li key={m.marca} className="flex items-center gap-2 text-sm">
            <span className="h-3 w-3 shrink-0 rounded-sm"
              style={{ background: COLORES_GONDOLA[m.digito - 1] ?? '#8A959A' }} />
            <span className="w-36 shrink-0">{m.marca}</span>
            <span className="h-2 rounded bg-gray-200" style={{ width: `${Math.max(1, m.pct * 1.6)}px` }} />
            <b className="tabular-nums">{m.pct} %</b>
            <span className="text-xs text-gray-500">({m.celdas} celdas)</span>
          </li>
        ))}
      </ul>

      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-4">
        <Dato etiqueta="Participación de P&G">
          <b className="tabular-nums">{pg.pct} %</b>
          <span className="block text-xs text-gray-500">
            {g.pg.join(' + ')} · {pg.celdas} de {pg.pintadas} celdas pintadas
          </span>
        </Dato>
        <Dato etiqueta="A la altura de los ojos">
          {!ojos.aplica
            ? <span className="text-gray-500">no aplica: el mueble tiene 3 niveles</span>
            : ojos.medida
              ? <><b className="tabular-nums">{ojos.pct} %</b> <span className="text-xs text-gray-500">de P&G</span></>
              : SIN}
        </Dato>
        <Dato etiqueta="El bloque de P&G está en">
          {trozos === 0 ? SIN : `${trozos} ${trozos === 1 ? 'pedazo' : 'pedazos'}`}
        </Dato>
        <Dato etiqueta="Del mueble se pintó">
          <span className="tabular-nums">{p.pctPintado} %</span>
          <span className="block text-xs text-gray-500">{p.pintadas} de {p.celdas} celdas</span>
        </Dato>
      </dl>
      <p className="text-xs text-gray-500">
        Los porcentajes de marca se reparten sobre las <b>celdas pintadas</b>, no sobre el mueble
        entero. Con el redondeo la suma puede dar 99 o 101 y no se fuerza a 100.
      </p>
    </div>
  );
}

function BloqueGondola({ g, gd }: { g: GondolaCat; gd: GondolaJson }) {
  const items = gd.items ?? {};
  const marcados = Object.keys(items).filter((k) => items[k]);
  const precios = gd.precios ?? [];
  const promos = gd.promos ?? [];
  const pop = gd.pop ?? [];
  const revisadaOk = gd.revisada === 'Sí, la revisé toda';

  return (
    <Seccion titulo={`La góndola de ${g.nom.toLowerCase()}`} ancho>
      <div className="space-y-4">
        {/* La foto del mueble entero va ARRIBA de la grilla, y es lo que permite auditarla: sin
            ella, un 100 % de una marca no se puede contrastar con nada. */}
        <Fotos fotos={gd.fotos} alt={`Góndola de ${g.nom}`} />

        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-4">
          <Dato etiqueta="¿Terminaste de revisar toda la lista?">{texto(gd.revisada)}</Dato>
          <Dato etiqueta="¿Hay carro de reposición en el pasillo?">{texto(gd.carro)}</Dato>
          <Dato etiqueta="¿Cuántos módulos tiene?">{gd.modulos ?? SIN}</Dato>
          <Dato etiqueta="¿Cuántas repisas tiene cada módulo?">{gd.niveles ?? SIN}</Dato>
        </dl>

        <div>
          <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Cuánto ocupa cada marca
          </h5>
          <Grilla g={g} gd={gd} />
        </div>

        <div>
          <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Qué productos están
            <span className="ml-2 font-normal normal-case text-gray-500">
              {marcados.length} de {g.grupos.reduce((s, x) => s + x.items.length, 0)}
            </span>
          </h5>
          {!revisadaOk && (
            <p className="mb-2">
              <Senal>lo que no está marcado puede ser «no lo miré»</Senal>
            </p>
          )}
          <div className="space-y-2">
            {g.grupos.map((gr) => (
              <div key={gr.titulo}>
                <p className="text-xs text-gray-600">{gr.titulo}</p>
                <div className="mt-0.5 flex flex-wrap gap-1">
                  {gr.items.map((it) => (
                    <span key={it}
                      className={`rounded px-1.5 py-0.5 text-xs ${items[it] === true
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-400 line-through'}`}>
                      {it}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* Los dos campos libres son la puerta de entrada de lo que el catálogo de 2026 no
              anticipó: es el dato que hace que la lista cerrada del año que viene sea distinta. */}
          <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Dato etiqueta="¿Viste algún producto de P&G que no esté en la lista?">
              {texto(gd.otros_pg)}
            </Dato>
            <Dato etiqueta="¿Viste alguna marca de la competencia que no esté?">
              {texto(gd.otros_comp)}
            </Dato>
          </dl>
        </div>

        {pop.length > 0 && (
          <SeccionPlegable
            titulo="¿Qué material publicitario hay en esta góndola?"
            extra={<span className="text-xs text-gray-500">{pop.length}</span>}
          >
            <ul className="space-y-2">
              {pop.map((x, i) => (
                <li key={i} className="rounded border border-gray-200 p-2">
                  <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                    <Dato etiqueta="Qué es">{texto(x.tipo)}</Dato>
                    <Dato etiqueta="¿De qué marca?">{texto(x.marca)}</Dato>
                  </dl>
                  <div className="mt-2"><Fotos fotos={x.fotos} alt={`POP ${i + 1}`} /></div>
                </li>
              ))}
            </ul>
          </SeccionPlegable>
        )}

        {promos.length > 0 && (
          <SeccionPlegable
            titulo="Promociones"
            extra={<span className="text-xs text-gray-500">{promos.length}</span>}
          >
            <ul className="space-y-2">
              {promos.map((x, i) => (
                <li key={i} className="rounded border border-gray-200 p-2">
                  <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
                    <Dato etiqueta="¿De qué marca es el cartel?">{texto(x.marca)}</Dato>
                    <Dato etiqueta="¿Qué dice el cartel?">{texto(x.mecanica)}</Dato>
                    <Dato etiqueta="¿Exige tarjeta de la cadena?">{texto(x.tarjeta)}</Dato>
                  </dl>
                  <div className="mt-2"><Fotos fotos={x.fotos} alt={`Promoción ${i + 1}`} /></div>
                </li>
              ))}
            </ul>
          </SeccionPlegable>
        )}

        {precios.length > 0 && (
          <SeccionPlegable
            titulo="Precios"
            extra={<span className="text-xs text-gray-500">
              {precios.filter((p) => p.tipo === 'choque').length} choques de {precios.length}
            </span>}
          >
            <ul className="space-y-2">
              {precios.map((p, i) => (
                <li key={i} className="rounded border border-gray-200 p-2">
                  <p className="mb-1 text-xs font-semibold text-gray-700">
                    {p.tipo === 'choque' ? `⚔ Choque ${i + 1}` : `Precio ${i + 1}`}
                  </p>
                  {/* Un choque dibujado como un precio suelto pierde justamente lo que lo hace un
                      choque: contra qué producto está peleando. Por eso las dos columnas. */}
                  <div className={p.tipo === 'choque' ? 'grid gap-4 sm:grid-cols-2' : ''}>
                    <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                      <Dato etiqueta="Producto">{texto(p.producto)}</Dato>
                      <Dato etiqueta="Precio normal">{texto(p.normal)}</Dato>
                      <Dato etiqueta="Precio más bajo">{texto(p.bajo)}</Dato>
                      <Dato etiqueta="¿Bajo qué condición aplica el más bajo?">
                        {texto(p.condicion)}
                      </Dato>
                    </dl>
                    {p.tipo === 'choque' && (
                      <dl className="grid gap-x-6 gap-y-1 rounded bg-gray-50 p-2 sm:grid-cols-2">
                        <Dato etiqueta="El producto de enfrente">{texto(p.rival)}</Dato>
                        <Dato etiqueta="Su precio normal">{texto(p.rival_normal)}</Dato>
                        <Dato etiqueta="Su precio más bajo">{texto(p.rival_bajo)}</Dato>
                        <Dato etiqueta="¿Por qué lo levantas?">{texto(p.motivo)}</Dato>
                      </dl>
                    )}
                  </div>
                  <div className="mt-2"><Fotos fotos={p.fotos} alt={`Etiqueta ${i + 1}`} /></div>
                </li>
              ))}
            </ul>
          </SeccionPlegable>
        )}

        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <Dato etiqueta="¿Hay impulsadoras en este pasillo?">
            {(gd.impulsadora ?? []).length === 0
              ? <span className="text-gray-500">no se contestó</span>
              : (
                <div className="flex flex-wrap gap-1">
                  {(gd.impulsadora ?? []).map((x) => <Senal key={x} tono="neutro">{x}</Senal>)}
                </div>
              )}
          </Dato>
          {/* Solo si sigue marcada «De la competencia»: el formulario NO limpia este texto al
              destildarla, así que mostrarlo siempre resucitaría una respuesta ya borrada. */}
          {(gd.impulsadora ?? []).includes('De la competencia') && (
            <Dato etiqueta="¿De qué marca es la de la competencia?">
              {texto(gd.impulsadora_marca)}
            </Dato>
          )}
          <Dato etiqueta="Algo que un número no cuente">{texto(gd.obs)}</Dato>
        </dl>
      </div>
    </Seccion>
  );
}

/** El súper detalle de una ficha de canal moderno, con los rótulos exactos del formulario. */
export default function DetalleModerno({ ficha: f }: { ficha: FichaModerno }) {
  const j = f.ficha;
  const gs = j.gondolas ?? {};
  const exhibiciones = j.cierre?.exhibiciones ?? [];

  return (
    <>
      <Seccion titulo="La visita" ancho>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <Dato etiqueta="Tienda">
            {nombreDeTienda(f)}
            {fueraDeLaMaestra(f) && <> <Senal>escrita a mano</Senal></>}
          </Dato>
          <Dato etiqueta="Cadena">{texto(f.cadena)}</Dato>
          <Dato etiqueta="Supervisor">{texto(f.supervisor)}</Dato>
          <Dato etiqueta="Coordenadas">
            {f.lat === null || f.lng === null ? <Senal tono="neutro">sin GPS</Senal> : (
              <a className="text-blue-700 underline" target="_blank" rel="noreferrer"
                href={`https://www.google.com/maps?q=${f.lat},${f.lng}`}>
                {f.lat.toFixed(5)}, {f.lng.toFixed(5)}
              </a>
            )}
          </Dato>
          <Dato etiqueta="Se envió">
            {f.origin === 'offline' ? 'sin señal, desde la cola' : 'con señal'}
          </Dato>
          <Dato etiqueta="Llegó al servidor">
            {f.registradoEn === null ? SIN
              : formatInTimeZone(new Date(f.registradoEn), TZ, 'dd/MM/yyyy HH:mm')}
          </Dato>
        </dl>
      </Seccion>

      <Seccion titulo="Al entrar a la tienda" ancho>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <Dato etiqueta="Desde que entraste hasta llegar al pasillo, ¿viste publicidad de estas marcas?">
            {texto(j.entrada?.publicidad_camino)}
          </Dato>
          {/* Solo si contestó «Sí, vi»: el formulario deja el texto escrito al cambiar de opción. */}
          {j.entrada?.publicidad_camino === 'Sí, vi' && (
            <Dato etiqueta="¿De qué marca y dónde?">{texto(j.entrada.publicidad_marca)}</Dato>
          )}
        </dl>
      </Seccion>

      {GONDOLAS.map((g) => {
        const gd = gs[g.id];
        if (gd === undefined) {
          return (
            <Seccion key={g.id} titulo={`La góndola de ${g.nom.toLowerCase()}`} ancho>
              <p className="text-sm text-gray-600">No se registró esta góndola en la visita.</p>
            </Seccion>
          );
        }
        return <BloqueGondola key={g.id} g={g} gd={gd} />;
      })}

      <Seccion titulo="Vuelta por la tienda" extra={`${exhibiciones.length}`} ancho>
        {exhibiciones.length === 0 ? (
          <p className="text-sm text-gray-600">
            No se registró ninguna exhibición fuera del pasillo. Cero es un hallazgo, no un hueco.
          </p>
        ) : (
          <ul className="space-y-2">
            {exhibiciones.map((x, i) => (
              <li key={i} className="rounded border border-gray-200 p-2">
                <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
                  <Dato etiqueta="¿Qué es?">{texto(x.tipo)}</Dato>
                  <Dato etiqueta="¿Dónde estaba?">{texto(x.ubic)}</Dato>
                  <Dato etiqueta="¿De qué marca?">{texto(x.marca)}</Dato>
                </dl>
                <div className="mt-2"><Fotos fotos={x.fotos} alt={`Exhibición ${i + 1}`} /></div>
              </li>
            ))}
          </ul>
        )}
      </Seccion>

      <Seccion titulo="Cerrar la visita" ancho>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <Dato etiqueta="¿Pudiste medir las dos góndolas?">{texto(j.cierre?.completo)}</Dato>
          <Dato etiqueta="Si alguna foto no se pudo tomar, ¿por qué?">
            {texto(j.cierre?.sin_foto)}
            {j.cierre?.sin_foto === 'Me pidieron no fotografiar' && (
              <span className="block text-xs text-gray-500">
                No es una falla del equipo: es una tienda que no dejó levantar.
              </span>
            )}
          </Dato>
          <Dato etiqueta="Algo más que quieras dejar dicho">{texto(j.cierre?.obs)}</Dato>
        </dl>
      </Seccion>
    </>
  );
}
